import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  HiOutlineCalendar, 
  HiOutlineOfficeBuilding, 
  HiOutlineMail, 
  HiOutlinePhone,
  HiOutlineDocumentText,
  HiOutlineStar
} from 'react-icons/hi';
import './Convocatorias.css';

const CMS_API_URL = process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api';
const MEDIA_URL = (process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

export default function Convocatorias() {
  const [convocatorias, setConvocatorias] = useState([]);
  const [convocatoriaActiva, setConvocatoriaActiva] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const cargarConvocatorias = useCallback(async () => {
    try {
      const response = await axios.get(`${CMS_API_URL}/announcements?active=true&lang=${lang}`);
      const convocatoriasData = response.data.announcements || [];
      setConvocatorias(convocatoriasData);
    } catch (error) {
      console.error('Error al cargar convocatorias:', error);
      // Si falla, mostrar array vacío
      setConvocatorias([]);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    cargarConvocatorias();
  }, [cargarConvocatorias]);

  const getTituloConvocatoria = (conv) => {
    return lang === 'en' ? conv.title_en : conv.title_es;
  };

  const getDescripcionConvocatoria = (conv) => {
    return lang === 'en' ? conv.description_en : conv.description_es;
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString(lang === 'en' ? 'en-US' : 'es-MX', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const estaVigente = (deadline) => {
    if (!deadline) return true;
    const hoy = new Date();
    const fechaLimite = new Date(deadline);
    return fechaLimite >= hoy;
  };

  const getEstadoConvocatoria = (conv) => {
    if (!conv.is_active) {
      return {
        texto: lang === 'en' ? 'Inactive' : 'Inactiva',
        clase: 'estado-inactiva'
      };
    }

    if (!conv.deadline) {
      return {
        texto: lang === 'en' ? 'Active' : 'Vigente',
        clase: 'estado-vigente'
      };
    }

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaLimite = new Date(conv.deadline);
    fechaLimite.setHours(0, 0, 0, 0);
    const diasRestantes = Math.ceil((fechaLimite - hoy) / (1000 * 60 * 60 * 24));

    if (diasRestantes < 0) {
      return {
        texto: lang === 'en' ? 'Expired' : 'Vencida',
        clase: 'estado-vencida'
      };
    } else if (diasRestantes <= 7) {
      return {
        texto: lang === 'en' ? `Expires in ${diasRestantes}d` : `Vence en ${diasRestantes}d`,
        clase: 'estado-por-vencer'
      };
    } else {
      return {
        texto: lang === 'en' ? 'Active' : 'Vigente',
        clase: 'estado-vigente'
      };
    }
  };

  if (loading) {
    return (
      <div className="convocatorias-section">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t("convocatorias.cargando")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="convocatorias-section">
      <h2 className="titulo-convocatorias">{t("convocatorias.titulo")}</h2>
      
      {convocatorias.length === 0 ? (
        <div className="no-convocatorias">
          <p>{t("convocatorias.noDisponibles")}</p>
        </div>
      ) : (
        <div className="tarjetas-container">
          {convocatorias.map(conv => {
            const estado = getEstadoConvocatoria(conv);
            const esVencida = conv.deadline && !estaVigente(conv.deadline);
            const esInactiva = !conv.is_active;
            const tieneOverlay = esVencida || esInactiva;
            
            return (
              <div className="tarjeta-convocatoria" key={conv.id}>
                <div className="convocatoria-imagen-wrapper">
                  {conv.featured_image && (
                    <img 
                      src={`${MEDIA_URL}${conv.featured_image}`} 
                      alt={getTituloConvocatoria(conv)}
                      className={tieneOverlay ? (esInactiva ? 'imagen-inactiva' : 'imagen-vencida') : ''}
                      onError={(e) => {
                        e.target.src = '/imagenes/Convocatorias/Conv1.png';
                      }}
                    />
                  )}
                  {esInactiva && (
                    <div className="overlay-inactiva">
                      <span className="texto-inactiva">{estado.texto}</span>
                    </div>
                  )}
                  {esVencida && !esInactiva && (
                    <div className="overlay-vencida">
                      <span className="texto-vencida">{estado.texto}</span>
                    </div>
                  )}
                  <span className={`badge-estado ${estado.clase}`}>{estado.texto}</span>
                </div>
                <div className="convocatoria-info">
                  <h3>{getTituloConvocatoria(conv)}</h3>
                  {conv.deadline && (
                    <p className="convocatoria-deadline">
                      <HiOutlineCalendar style={{display: 'inline', marginRight: '6px'}} /> <strong>{t("convocatorias.fechaLimite")}:</strong> {formatFecha(conv.deadline)}
                    </p>
                  )}
                  {conv.organization && (
                    <p className="convocatoria-org"><HiOutlineOfficeBuilding style={{display: 'inline', marginRight: '6px'}} /> {conv.organization}</p>
                  )}
                  {conv.contact_email && (
                    <p className="convocatoria-contacto"><HiOutlineMail style={{display: 'inline', marginRight: '6px'}} /> {conv.contact_email}</p>
                  )}
                  <button onClick={() => setConvocatoriaActiva(conv)}>
                    {t("convocatorias.ver")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {convocatoriaActiva && (
        <div className="modal-overlay" onClick={() => setConvocatoriaActiva(null)}>
          <div className="modal modal-convocatoria" onClick={(e) => e.stopPropagation()}>
            <div className="modal-imagen-wrapper">
              {convocatoriaActiva.featured_image && (
                <img 
                  src={`${MEDIA_URL}${convocatoriaActiva.featured_image}`} 
                  alt={getTituloConvocatoria(convocatoriaActiva)}
                  className={(!estaVigente(convocatoriaActiva.deadline) || !convocatoriaActiva.is_active) ? 
                    (!convocatoriaActiva.is_active ? 'imagen-inactiva' : 'imagen-vencida') : ''}
                />
              )}
              {!convocatoriaActiva.is_active && (
                <div className="overlay-inactiva-modal">
                  <span className="texto-inactiva-grande">{lang === 'en' ? 'INACTIVE' : 'INACTIVA'}</span>
                </div>
              )}
              {!estaVigente(convocatoriaActiva.deadline) && convocatoriaActiva.deadline && convocatoriaActiva.is_active && (
                <div className="overlay-vencida-modal">
                  <span className="texto-vencida-grande">{lang === 'en' ? 'EXPIRED' : 'VENCIDA'}</span>
                </div>
              )}
              <span className={`badge-estado-modal ${getEstadoConvocatoria(convocatoriaActiva).clase}`}>
                {getEstadoConvocatoria(convocatoriaActiva).texto}
              </span>
            </div>
            <h3>{getTituloConvocatoria(convocatoriaActiva)}</h3>
            <p className="modal-descripcion">{getDescripcionConvocatoria(convocatoriaActiva)}</p>
            
            <div className="modal-detalles">
              {convocatoriaActiva.deadline && (
                <p className="modal-info">
                  <HiOutlineCalendar style={{display: 'inline', marginRight: '6px'}} /> <strong>{t("convocatorias.fechaLimite")}:</strong> {formatFecha(convocatoriaActiva.deadline)}
                </p>
              )}
              
              {convocatoriaActiva.organization && (
                <p className="modal-info">
                  <HiOutlineOfficeBuilding style={{display: 'inline', marginRight: '6px'}} /> <strong>{t("convocatorias.organizacion")}:</strong> {convocatoriaActiva.organization}
                </p>
              )}
              
              {convocatoriaActiva.contact_email && (
                <p className="modal-info">
                  <HiOutlineMail style={{display: 'inline', marginRight: '6px'}} /> <strong>{t("convocatorias.contacto")}:</strong> {convocatoriaActiva.contact_email}
                </p>
              )}

              {convocatoriaActiva.contact_phone && (
                <p className="modal-info">
                  <HiOutlinePhone style={{display: 'inline', marginRight: '6px'}} /> <strong>{lang === 'en' ? 'Phone' : 'Teléfono'}:</strong> {convocatoriaActiva.contact_phone}
                </p>
              )}
            </div>

            {convocatoriaActiva.requirements && (
              <div className="modal-section">
                <strong><HiOutlineDocumentText style={{display: 'inline', marginRight: '6px'}} /> {t("convocatorias.requisitos")}:</strong>
                <p>{convocatoriaActiva.requirements}</p>
              </div>
            )}

            {convocatoriaActiva.benefits && (
              <div className="modal-section">
                <strong><HiOutlineStar style={{display: 'inline', marginRight: '6px'}} /> {t("convocatorias.beneficios")}:</strong>
                <p>{convocatoriaActiva.benefits}</p>
              </div>
            )}

            <div className="modal-actions">
              {convocatoriaActiva.registration_url && (
                <a 
                  href={convocatoriaActiva.registration_url}
                  className="boton-registrar"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("convocatorias.registrarse")}
                </a>
              )}
              
              {convocatoriaActiva.document_url && (
                <a 
                  href={`${MEDIA_URL}${convocatoriaActiva.document_url}`} 
                  download 
                  className="boton-descargar"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("convocatorias.descargar")}
                </a>
              )}
            </div>

            <button className="cerrar" onClick={() => setConvocatoriaActiva(null)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}